import { computed, useId, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNxToasts } from '@blpde/nx-ui';
import { dictionaryOwnDocument } from '../../config/dictionaryLayer';
import type { JsonObject } from '../../config/mergePatch';
import { originKey, reportWriteFailure, resolveWriteFailure } from '../../http';
import { dictionaryWriteFailure, type DictionaryLevelHandle, type DictionaryWriteResult } from './level';
import { DICTIONARY_TEXT } from './labels';

/** Why a tab cannot be edited: a layer before locked its field, or master refused the permission. */
export type DictionaryReadOnly = 'company' | 'tenant' | 'inherited' | 'forbidden' | null;

export interface DictionaryTabState {
  /** The lock or refusal that makes the tab read-only, if any. */
  readOnlyReason: ComputedRef<DictionaryReadOnly>;
  readOnly: ComputedRef<boolean>;
  /** The written layer is known: removal and the own/inherited split can be trusted. */
  layersKnown: ComputedRef<boolean>;
  /** The written layer's own document of the key (undefined while the layers are not known). */
  ownDocument: ComputedRef<JsonObject | undefined>;
  /** The written layer locks the field for every later layer (admin door only). */
  ownLocked: ComputedRef<boolean>;
  /** The text of the read-only banner, if any. */
  readOnlyText: ComputedRef<string | null>;
  /** Writes one patch; a refusal shows as the standard write-failure toast. */
  write: (patch: object) => Promise<boolean>;
  /** Sets the written layer's lock of the field; a refusal toasts. */
  setOwnLock: (locked: boolean) => Promise<boolean>;
}

/**
 * What every tab of the dictionary derives from its level for one top-level
 * field: whether the field is read-only (locked before the written layer, or
 * master refused the permission), the written layer's own document, and the
 * writers whose refusals show as the one standard toast (I-2813).
 */
export function useDictionaryTabState(level: DictionaryLevelHandle<unknown>, field: string): DictionaryTabState {
  const { t } = useI18n();
  const { showToast, dismiss } = useNxToasts();
  const origin = originKey(useId(), 'action', `write-${field}`);

  const readOnlyReason = computed<DictionaryReadOnly>(() => {
    const locking = level.enforced.value.find((entry) => entry.field === field);
    if (locking) {
      if (locking.subjectType === 'company') return 'company';
      if (locking.subjectType === 'tenant') return 'tenant';
      return 'inherited';
    }
    return level.forbidden?.value ? 'forbidden' : null;
  });
  const layersKnown = computed(() => level.layers.value !== null && level.target.value !== null);

  async function report(result: DictionaryWriteResult): Promise<boolean> {
    if (result.ok) {
      resolveWriteFailure({ origin });
      return true;
    }
    reportWriteFailure(dictionaryWriteFailure(result), { origin, t, toast: { showToast, dismiss }, kind: 'update' });
    return false;
  }

  return {
    readOnlyReason,
    readOnly: computed(() => readOnlyReason.value !== null),
    layersKnown,
    ownDocument: computed(() =>
      layersKnown.value ? dictionaryOwnDocument(level.layers.value, level.target.value) : undefined,
    ),
    ownLocked: computed(() => level.locks?.value.includes(field) ?? false),
    readOnlyText: computed(() =>
      readOnlyReason.value ? t(`${DICTIONARY_TEXT}.readOnly.${readOnlyReason.value}`) : null,
    ),
    write: async (patch) => report(await level.patch(patch)),
    setOwnLock: async (locked) => {
      if (!level.setLock) return false;
      return report(await level.setLock(field, locked));
    },
  };
}
